import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { VerifiedEntry } from '@/types/api';
import { useCreateMissingEntry } from '@/hooks/useLibraryReview';
import { useUpdateEntry } from '@/hooks/useEntries';

interface MissingEntryDialogProps {
  open: boolean;
  libraryId: number;
  existingEntries: VerifiedEntry[];
  mode?: 'create' | 'update';
  entryToUpdate?: VerifiedEntry | null;
  initialMsNo?: string;
  initialTitle?: string;
  initialAuthor?: string;
  onClose: () => void;
  onCreated?: (entry: VerifiedEntry) => void;
  onUpdated?: (entry: VerifiedEntry) => void;
}

const MissingEntryDialog: React.FC<MissingEntryDialogProps> = ({
  open,
  libraryId,
  existingEntries,
  mode = 'create',
  entryToUpdate,
  initialMsNo,
  initialTitle,
  initialAuthor,
  onClose,
  onCreated,
  onUpdated,
}) => {
  const createMissingEntry = useCreateMissingEntry();
  const updateEntry = useUpdateEntry();

  const [msNo, setMsNo] = useState('');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [notes, setNotes] = useState('');
  const [position, setPosition] = useState<'auto' | 'after_entry' | 'before_entry'>('auto');
  const [referenceEntry, setReferenceEntry] = useState<VerifiedEntry | null>(null);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (mode === 'update' && entryToUpdate) {
      setMsNo(entryToUpdate.ms_no || '');
      setTitle(entryToUpdate.title || '');
      setAuthor(entryToUpdate.author || '');
      setNotes(entryToUpdate.missing_notes || '');
      setPosition('auto');
      setReferenceEntry(null);
      setValidationError('');
      return;
    }

    setMsNo(initialMsNo || '');
    setTitle(initialTitle || '');
    setAuthor(initialAuthor || '');
    setNotes('');
    setPosition('auto');
    setReferenceEntry(null);
    setValidationError('');
  }, [mode, entryToUpdate, initialMsNo, initialTitle, initialAuthor]);

  const entryOptions = useMemo(() => existingEntries.filter((e) => !e.is_missing), [existingEntries]);

  const handleCreate = async () => {
    // Validate required fields
    if (!msNo.trim()) {
      setValidationError('Manuscript Number is required');
      return;
    }
    if (!notes.trim()) {
      setValidationError('Notes are required - please explain why this manuscript is missing');
      return;
    }

    setValidationError('');

    if (mode === 'update' && entryToUpdate) {
      const updated = await updateEntry.mutateAsync({
        id: entryToUpdate.id,
        data: {
          is_missing: true,
          missing_notes: notes.trim(),
        },
      });
      onUpdated?.(updated);
      onClose();
      return;
    }

    const payload = {
      library_id: libraryId,
      ms_no: msNo.trim(),
      title: title.trim() || undefined,
      author: author.trim() || undefined,
      missing_notes: notes.trim(),
      insert_after_entry_id: position === 'after_entry' ? referenceEntry?.id : undefined,
      insert_before_entry_id: position === 'before_entry' ? referenceEntry?.id : undefined,
    };

    const created = await createMissingEntry.mutateAsync(payload);
    onCreated?.(created);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'update' ? 'Mark Entry Missing' : 'Mark Missing Manuscript'}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          This creates a placeholder for a manuscript that should exist but is not in the collection.
        </Alert>

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <TextField
          label="Manuscript Number"
          value={msNo}
          onChange={(e) => {
            setMsNo(e.target.value);
            if (validationError) setValidationError('');
          }}
          helperText="E.g., '5' if between MS #4 and #6"
          required
          fullWidth
          margin="normal"
          disabled={mode === 'update'}
          error={validationError.includes('Manuscript Number')}
        />

        <TextField
          label="Title (if known)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          helperText="Optional: Expected title based on sequence"
          fullWidth
          margin="normal"
          inputProps={{ dir: 'rtl' }}
          disabled={mode === 'update'}
        />

        <TextField
          label="Author"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          helperText="Usually same as other entries in library"
          fullWidth
          margin="normal"
          inputProps={{ dir: 'rtl' }}
          disabled={mode === 'update'}
        />

        {mode === 'create' && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>Insert Position</InputLabel>
              <Select
                value={position}
                onChange={(e) => setPosition(e.target.value as 'auto' | 'after_entry' | 'before_entry')}
                label="Insert Position"
              >
                <MenuItem value="auto">Auto-detect from MS number</MenuItem>
                <MenuItem value="after_entry">After specific entry...</MenuItem>
                <MenuItem value="before_entry">Before specific entry...</MenuItem>
              </Select>
            </FormControl>

            {position !== 'auto' && (
              <Autocomplete
                options={entryOptions}
                getOptionLabel={(entry) => `MS ${entry.ms_no || '—'}: ${entry.title}`}
                renderInput={(params) => (
                  <TextField {...params} label="Select Entry" margin="normal" />
                )}
                onChange={(_, entry) => setReferenceEntry(entry)}
              />
            )}
          </>
        )}

        <TextField
          label="Notes"
          multiline
          rows={3}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            if (validationError) setValidationError('');
          }}
          helperText="Why is this manuscript missing?"
          required
          fullWidth
          margin="normal"
          error={validationError.includes('Notes')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="warning"
          disabled={createMissingEntry.isPending || updateEntry.isPending}
        >
          {mode === 'update' ? 'Mark Missing' : 'Mark as Missing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissingEntryDialog;
